import React, { ReactNode, useState } from "react";
import { Modal, Button, message as AntMessage } from "antd";
import { formatCurrency } from "../../utils/basic.utils";
import "./User-Profile-Comp.css";
import { IUserTransactionsTypeData } from "../../apiservice/userService.type";
import { convertToLongDate } from "../../utils/date.utils";
import { sampleApiCall } from "../../apiservice/authService";
import { appZIndex } from "../../utils/appconst";
import { userGetTransactionDispute } from "../../apiservice/userService";

// Define prop types
type ITransactionCard = {
  transactionDetailsData?: IUserTransactionsTypeData | null;
};

const UserTransactionsDetailsCard: React.FC<ITransactionCard> = ({
  transactionDetailsData,
}) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDisputeClick = () => {
    setIsPopupVisible(true);
  };

  const handleDisputeConfirm = async () => {
    if (!transactionDetailsData?.id) return;

    setIsSubmitting(true);

    try {
      // Call the dispute API
      await userGetTransactionDispute({ transactionId: transactionDetailsData.id, description: "There is a dispute with this transactions" });
      showAlert("Success", "Dispute has been sent successfully.");
    } catch (error) {
      showAlert("Error", "Failed to send dispute. Please try again later.");
    } finally {
      setIsSubmitting(false);
      setIsPopupVisible(false);
    }
  };

  const showAlert = (title: string, content: ReactNode) => {
    Modal.info({
      title,
      content,
      onOk() { },
      zIndex: appZIndex.modal,
    });
  };

  return (
    <div className="flex flex-col p-4 bg-white dark:bg-stone-800 border border-gray-300 dark:border-stone-700 rounded-lg mb-4">
      {/* Header */}
      <div className="flex items-center mb-4">
        <h2 className="flex-grow pt-2 pb-2 font-sans text-lg font-semibold text-gray-900 dark:text-stone-100">
          Transaction Details
        </h2>
      </div>

      {/* Transaction Details */}
      <div className="flex flex-col text-gray-700 dark:text-stone-300">
        <p className="mt-4">
          <strong className="dark:text-stone-200">Transaction ID:</strong> {transactionDetailsData?.id || "N/A"}
        </p>
        <p className="mt-4">
          <strong className="dark:text-stone-200">Description:</strong>{" "}
          {transactionDetailsData?.description || "N/A"}
        </p>
        <p className="mt-4">
          <strong className="dark:text-stone-200">Type:</strong> {transactionDetailsData?.type || "N/A"}
        </p>
        <p className="mt-4">
          <strong className="dark:text-stone-200">Entry:</strong> {transactionDetailsData?.entry || "N/A"}
        </p>
        <p
          className="mt-4" // Main text color inherited from parent div
          style={{
            color: (transactionDetailsData?.amount || 0) > 0 ? "green" : "red", // Consider dark:text-green-400 dark:text-red-400 if this doesn't have enough contrast
          }}
        >
          <strong className="dark:text-stone-200">Amount:</strong>{" "}
          {formatCurrency(transactionDetailsData?.amount)}
        </p>
        <p className="mt-4">
          <strong className="dark:text-stone-200">Verified:</strong>{" "}
          {transactionDetailsData?.verified ? "Yes" : "No"}
        </p>
        <p className="mt-4">
          <strong className="dark:text-stone-200">Date Created:</strong>{" "}
          {convertToLongDate(transactionDetailsData?.dateCreated || "") ||
            "N/A"}
        </p>

        {/* Conditional Rendering for TransferOut3P */}
        {transactionDetailsData?.transferOut3P && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-stone-100">Transfer Details</h3>
            <p className="mt-4"> {/* text-gray-700 dark:text-stone-300 inherited */}
              <strong className="dark:text-stone-200">Account Number:</strong>{" "}
              {transactionDetailsData.transferOut3P.accountNumber}
            </p>
            <p className="mt-4"> {/* text-gray-700 dark:text-stone-300 inherited */}
              <strong className="dark:text-stone-200">Bank Code:</strong>{" "}
              {transactionDetailsData.transferOut3P.bankCode}
            </p>
          </div>
        )}

        {/* Dispute Transaction Button */}
        <button
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-400"
          onClick={handleDisputeClick}
        >
          Dispute Transaction
        </button>

        {/* Ant Design Modal */}
        <Modal
          title="Dispute Transaction"
          visible={isPopupVisible}
          onOk={handleDisputeConfirm}
          onCancel={() => setIsPopupVisible(false)}
          confirmLoading={isSubmitting}
          okText="Okay"
          cancelText="Cancel"
          zIndex={appZIndex.modal}
        >
          <p>Are you sure you want to dispute this transaction?</p>
        </Modal>
      </div>
    </div>
  );
};

export default UserTransactionsDetailsCard;
